/**
 * Monte Carlo card (spec §14): worker-hosted trials, fan chart, failure-year
 * distribution, flexible spending, and the deterministic sequence-of-returns
 * stress test. The success figure is presented with humility, once.
 */
import { useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, BarChart, Bar,
} from 'recharts';
import type { Plan } from '../model/types';
import type { MonteCarloResult } from '../engine/montecarlo';
import { stressTestSequence, HISTORICAL_MEANS } from '../engine/montecarlo';
import { runProjection, activeAssumptions } from '../engine/projection';
import { fmtCompactUsd, fmtUsd, fmtPct } from './format';

interface Props {
  plan: Plan;
  /**
   * Follows the app-wide dollars switch rather than owning a second one: two
   * controls that can disagree is worse than one, and the card would otherwise
   * silently contradict the headline numbers above it.
   */
  todaysDollars: boolean;
}

/**
 * Colour bands for the adjustment odds. Advisers generally treat 75-90% as the
 * confidence zone, so 80 and 70 are the natural cut points: comfortable above
 * 80, worth watching in the 70s, needs a change below that. Deliberately no
 * separate colour above 90 — being very high is not a problem to fix, though
 * the note under the number mentions it can mean underspending.
 */
function adjustmentTone(rate: number): '' | ' warn' | ' negative' {
  if (rate >= 0.8) return '';
  if (rate >= 0.7) return ' warn';
  return ' negative';
}

/** 1st, 2nd, 3rd, 4th… — 11-13 are the exceptions that break the naive rule. */
function ordinal(n: number): string {
  const teen = n % 100;
  if (teen >= 11 && teen <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

export function MonteCarloCard({ plan, todaysDollars }: Props) {
  const [trials, setTrials] = useState(1_000);
  const [model, setModel] = useState<'bootstrap' | 'parametric'>('bootstrap');
  const [recenter, setRecenter] = useState(true);
  const [flexCut, setFlexCut] = useState(0.1);
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<MonteCarloResult | null>(null);
  const workerRef = useRef<Worker | null>(null);

  /**
   * Results are computed in today's dollars; multiplying by that year's
   * cumulative inflation gives the nominal figure. `atEnd` is for the terminal
   * numbers, which all land in the final plan year.
   */
  const inflateBy = (i: number) =>
    todaysDollars ? 1 : (result?.inflationFactors[i] ?? 1);
  const atEnd = todaysDollars
    ? 1
    : (result?.inflationFactors[result.inflationFactors.length - 1] ?? 1);
  const unitLabel = todaysDollars ? "today's dollars" : 'future (nominal) dollars';

  // What each model will actually assume stocks earn. Showing this on the
  // control is the whole fix for "why do the two disagree so wildly?".
  const profileEquity = activeAssumptions(plan).returnsByAssetClass.equity;
  const equityMeanToUse =
    model === 'bootstrap' && !recenter ? HISTORICAL_MEANS.equity : profileEquity;

  // The straight-line plan in today's dollars, to overlay on the fan chart —
  // the same run that produces the numbers at the top of the screen.
  const deterministicPath = useMemo(() => {
    const r = runProjection(plan, 2026);
    return r.snapshots.map((s) => Math.max(0, s.netWorth / s.cumulativeInflationFactor));
  }, [plan]);

  // The deterministic stress test is cheap — always shown.
  const stress = useMemo(() => {
    const seq = stressTestSequence(plan);
    const r = runProjection(plan, 2026, { returnsByYear: seq });
    const last = r.snapshots[r.snapshots.length - 1]!;
    return {
      depletionYear: r.firstDepletionYear,
      endingToday: Math.max(0, last.netWorth / last.cumulativeInflationFactor),
    };
  }, [plan]);

  function run() {
    workerRef.current?.terminate();
    const worker = new Worker(new URL('../engine/mcWorker.ts', import.meta.url), {
      type: 'module',
    });
    workerRef.current = worker;
    setProgress(0);
    setResult(null);
    worker.onmessage = (e) => {
      if (e.data.type === 'progress') setProgress(e.data.done / e.data.total);
      if (e.data.type === 'result') {
        setResult(e.data.result);
        setProgress(null);
        worker.terminate();
      }
    };
    worker.postMessage({
      plan,
      options: { trials, model, recenter, flexibleSpendingCut: flexCut, seed: 20260803 },
    });
  }

  const failureHistogram = useMemo(() => {
    if (!result || result.failureYears.length === 0) return [];
    const counts = new Map<number, number>();
    for (const y of result.failureYears) {
      const bucket = Math.floor(y / 2) * 2; // 2-year buckets
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([year, count]) => ({ year: `${year}`, count }));
  }, [result]);

  return (
    <section className="card" aria-label="Monte Carlo simulation">
      <h2 className="section-title">
        How often would you need to adjust?
        <span className="note">
          Monte Carlo simulation · {trials.toLocaleString()} lifetimes ·{' '}
          {todaysDollars ? "today's dollars" : 'future dollars'} ·{' '}
          {model === 'bootstrap'
            ? 'replaying 8-year stretches of real 1928-2023 markets, so crashes and recoveries keep their real order'
            : 'drawing each year at random from a bell curve around your assumed returns'}
        </span>
      </h2>

      <div className="add-row" style={{ marginBottom: 'var(--space-2)' }}>
        <span className="seg" role="group" aria-label="Return model">
          <button aria-pressed={model === 'bootstrap'} onClick={() => setModel('bootstrap')}>
            Replay market history
          </button>
          <button aria-pressed={model === 'parametric'} onClick={() => setModel('parametric')}>
            Random years from your assumptions
          </button>
        </span>
        <select value={trials} onChange={(e) => setTrials(Number(e.target.value))} aria-label="Number of trials">
          <option value={1000}>1,000 trials</option>
          <option value={5000}>5,000 trials</option>
          <option value={10000}>10,000 trials</option>
        </select>
        <select value={flexCut} onChange={(e) => setFlexCut(Number(e.target.value))} aria-label="Flexible spending">
          <option value={0}>Rigid spending</option>
          <option value={0.1}>Cut fun spending 10% after down years</option>
          <option value={0.2}>Cut fun spending 20% after down years</option>
        </select>
        <button className="btn" onClick={run} disabled={progress != null}>
          {progress != null ? `Running… ${Math.round(progress * 100)}%` : result ? 'Run again' : 'Run simulation'}
        </button>
      </div>

      {/* Both models must run on the same expected return, or they answer
          different questions and look like they contradict each other. */}
      <p className="hint" style={{ marginTop: 0 }}>
        Both models compound stocks at <strong>{fmtPct(equityMeanToUse)}</strong> a year — the same
        rate the projection at the top of the screen uses
        {model === 'bootstrap' && recenter
          ? ', with history supplying the volatility, the crashes and their order.'
          : model === 'bootstrap'
            ? `. That is history's own rate, ${fmtPct(HISTORICAL_MEANS.equity - profileEquity)} above your Assumptions tab.`
            : '.'}
      </p>

      {model === 'bootstrap' && (
        <label className="mc-basis">
          <input
            type="checkbox"
            checked={!recenter}
            onChange={(e) => setRecenter(!e.target.checked)}
          />
          <span>
            Grow at history's own rate ({fmtPct(HISTORICAL_MEANS.equity)} compounded) instead of my
            assumption ({fmtPct(profileEquity)})
            <span className="hint" style={{ display: 'block' }}>
              US stocks compounded at {fmtPct(HISTORICAL_MEANS.equity)} over 1928-2023 — more than
              most plans assume going forward. Leaving this off keeps history's volatility and its
              crash sequences while holding growth to your own number, which is what lets these
              results line up with the rest of the tool. Turning it on bets that future returns
              match the past as closely as its volatility does.
            </span>
          </span>
        </label>
      )}

      {progress != null && (
        <div className="progress-track" role="progressbar" aria-valuenow={Math.round(progress * 100)}>
          <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>
      )}

      {result && (
        <>
          <div className="hero-row" style={{ marginBottom: 'var(--space-4)' }}>
            <div className="stat">
              <div className="label">Middle outcome at the end</div>
              <div className="value">
                {fmtCompactUsd((result.terminalWealths[Math.floor(result.terminalWealths.length / 2)] ?? 0) * atEnd)}
              </div>
              <div className="context">
                median of {result.trials.toLocaleString()} simulated lifetimes, in {unitLabel}
              </div>
            </div>
            <div className="stat">
              <div className="label">Never had to adjust</div>
              <div className={`value positive-scale${adjustmentTone(result.successRate)}`}>
                {Math.round(result.successRate * 100)}%
              </div>
              <div className="context">
                {result.successRate >= 0.999
                  ? 'the money outlasted the plan every time'
                  : `otherwise, spending fell short by about ${fmtUsd(Math.round(result.medianShortfallPerYear))}/yr in today's money${
                      result.medianShortfallAge != null ? `, starting around age ${result.medianShortfallAge}` : ''
                    }`}
              </div>
            </div>
            <div className="stat">
              <div className="label">Worst 10% of outcomes</div>
              <div className="value">
                {fmtCompactUsd((result.terminalWealths[Math.floor(result.terminalWealths.length * 0.1)] ?? 0) * atEnd)}
              </div>
              <div className="context">
                what a genuinely unlucky run leaves at the end — the number worth planning around
              </div>
            </div>
          </div>

          {/* Sequence risk matters, but a red headline overstates a single
              hand-picked scenario. It reads as a footnote instead. */}
          <p className="hint mc-stress">
            <strong>Sequence check:</strong> a −25%, −15%, −10% run in the first three retirement
            years — the classic plan-killer — leaves{' '}
            {stress.depletionYear
              ? `this plan short from ${stress.depletionYear}`
              : `${fmtCompactUsd(stress.endingToday * atEnd)} at the end`}
            . One deliberately bad ordering, not a probability.
          </p>

          {/* The reconciliation. Without this the top-of-screen numbers and the
              simulation look like they contradict each other. */}
          <div className="mc-reconcile">
            <strong>How this compares to the numbers at the top of the screen</strong>
            <p>
              Those come from a single straight line at {fmtPct(result.equityMeanUsed)} a year, every
              year.{' '}
              {result.deterministicTerminal <= 0 ? (
                <>
                  It runs the money out before the end, and so did{' '}
                  {Math.round((1 - result.successRate) * 100)}% of these simulated lifetimes — the
                  two views agree. What the simulation adds is the size of the gap and how early it
                  starts.
                </>
              ) : (
                <>
                  It ends at <strong>{fmtCompactUsd(result.deterministicTerminal * atEnd)}</strong> in
                  {' '}{unitLabel}, which lands at the{' '}
                  <strong>{ordinal(result.deterministicPercentile)} percentile</strong> of these{' '}
                  {result.trials.toLocaleString()} lifetimes.{' '}
                  {result.deterministicPercentile >= 60
                    ? "Real markets don't repeat the same return every year, and that unevenness costs you against a smooth ride — so the straight line is the luckier story."
                    : result.deterministicPercentile <= 40
                      ? 'Here the straight line is on the cautious side of what the simulations produced.'
                      : 'That is right around the middle, so the two views broadly agree.'}
                </>
              )}
            </p>
          </div>

          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <ComposedChart
                data={result.bands.map((b, i) => {
                  const f = inflateBy(i);
                  return {
                    year: b.year,
                    outer: [Math.round(b.p10 * f), Math.round(b.p90 * f)],
                    inner: [Math.round(b.p25 * f), Math.round(b.p75 * f)],
                    median: Math.round(b.p50 * f),
                    straightLine: Math.round((deterministicPath[i] ?? 0) * f),
                  };
                })}
                margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
              >
                <CartesianGrid stroke="var(--grid)" vertical={false} />
                <XAxis dataKey="year" stroke="var(--axis)" tickLine={false}
                  tick={{ fill: 'var(--ink-muted)', fontSize: 12 }} tickMargin={8} minTickGap={28} />
                <YAxis stroke="var(--axis)" tickLine={false} axisLine={false}
                  tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
                  tickFormatter={(v: number) => fmtCompactUsd(v)} width={64} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    borderRadius: 8, fontSize: 13, color: 'var(--ink)',
                  }}
                  formatter={(value) =>
                    Array.isArray(value)
                      ? `${fmtUsd(value[0] as number)} – ${fmtUsd(value[1] as number)}`
                      : fmtUsd(Number(value))}
                  labelStyle={{ color: 'var(--ink-secondary)', fontWeight: 600 }}
                />
                <Area dataKey="outer" name="10th–90th percentile" type="monotone"
                  fill="var(--series-1)" fillOpacity={0.15} stroke="none" />
                <Area dataKey="inner" name="25th–75th percentile" type="monotone"
                  fill="var(--series-1)" fillOpacity={0.25} stroke="none" />
                <Line dataKey="median" name="Median simulation" type="monotone"
                  stroke="var(--series-1)" strokeWidth={2} dot={false} />
                <Line dataKey="straightLine" name="Your straight-line plan" type="monotone"
                  stroke="var(--ink-secondary)" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {failureHistogram.length > 0 && (
            <>
              <h3 style={{ margin: 'var(--space-4) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
                When failures happen
              </h3>
              <p className="hint" style={{ marginTop: 0 }}>
                Running out at 91 and running out at 68 are entirely different problems — a single
                percentage hides the difference.
              </p>
              <div style={{ width: '100%', height: 140 }}>
                <ResponsiveContainer>
                  <BarChart data={failureHistogram} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <XAxis dataKey="year" stroke="var(--axis)" tickLine={false}
                      tick={{ fill: 'var(--ink-muted)', fontSize: 11 }} />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--surface-raised)', border: '1px solid var(--border)',
                        borderRadius: 8, fontSize: 13, color: 'var(--ink)',
                      }}
                    />
                    <Bar dataKey="count" name="Failures" fill="var(--series-2)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          <p className="disclaimer" style={{ marginTop: 'var(--space-3)' }}>
            Read the percentage as how often you'd get through untouched — the rest of the time you
            adjust, usually by trimming the discretionary half of the budget for a few years, not by
            going broke. There is no universal target: 85–90% is a reasonable bar for spending you
            can't cut, while 55–60% is fine for travel and extras, and a 99% plan often means
            underspending a life you already paid for. These are properties of these assumptions,
            not facts about the future.
            {!result.recentered && result.model === 'bootstrap' && (
              <>
                {' '}This run grew at history's {fmtPct(HISTORICAL_MEANS.equity)} rather than your
                {' '}{fmtPct(profileEquity)} assumption, so it reads more optimistically than
                everything else in the tool. That gap is the assumption, not the math.
              </>
            )}
          </p>
        </>
      )}
    </section>
  );
}
